import { useMemo } from 'react';
import ChapterSection from '../ChapterSection/ChapterSection';
import ChapterMap from '../ChapterMap/ChapterMap';
import type { GroundSummary, Point } from '@/lib/api.types';
import type { GeometryLayer, LegendItem, MapSpec } from '@/lib/map-spec';
import type { LeadSpec, RowSpec } from '@/lib/report-content';
import { TILES } from '@/lib/tiles';
import { useDevMode } from '@/hooks/useDevMode';
import { ARV_RANK_COLOR, ARV_RANK_LABEL, type ArvRank } from '@/lib/chapters';
import { ChapterLead, CheckList, RowList, rankToStatus, rankToStatusLabel } from './shared';
import styles from './shared.module.css';

type Props = {
  ground: GroundSummary;
  site: Point;
};

const SITE_LANDFORM_COLOR = '#B08B57';
const NEARBY_LANDFORM_PALETTE = ['#A8B86D', '#7AA8C7', '#9C7CB0', '#D9A441', '#C7A87A'];
const SUBMAP_HEIGHT = 220;
const CHECK_TITLE = '購入前に必ず確かめたいこと';

function colorForCategory(category: string | null, index: number): string {
  if (!category) return NEARBY_LANDFORM_PALETTE[index % NEARBY_LANDFORM_PALETTE.length];
  let hash = 0;
  for (let i = 0; i < category.length; i++) {
    hash = (hash * 31 + category.charCodeAt(i)) | 0;
  }
  const palette = NEARBY_LANDFORM_PALETTE;
  return palette[Math.abs(hash) % palette.length];
}

function buildLandformMap(ground: GroundSummary, site: Point): MapSpec {
  const gsi = ground.gsi_landform;
  const geos: GeometryLayer[] = [];
  const legend: LegendItem[] = [];

  const seenCategories = new Map<string, string>();
  (gsi.nearby_geometries ?? []).forEach((g, i) => {
    const category = (gsi.nearby_categories ?? [])[i] ?? null;
    const color = colorForCategory(category, i);
    geos.push({
      data: g,
      style: { stroke: false, fillColor: color, fillOpacity: 0.28 },
      label: category ? `自然地形: ${category}` : '自然地形 (周辺)',
    });
    if (category && !seenCategories.has(category)) {
      seenCategories.set(category, color);
    }
  });

  if (gsi.geometry) {
    geos.push({
      data: gsi.geometry,
      style: {
        stroke: false,
        fillColor: SITE_LANDFORM_COLOR,
        fillOpacity: 0.45,
      },
      label: gsi.category ? `この土地の自然地形: ${gsi.category}` : 'この土地の自然地形',
    });
    legend.push({ color: SITE_LANDFORM_COLOR, label: `この土地: ${gsi.category ?? '不明'}` });
  }
  for (const [category, color] of seenCategories) {
    legend.push({ color, label: `周辺: ${category}` });
  }
  return {
    key: 'landform',
    point: site,
    tileUrl: TILES.std,
    zoom: 15,
    height: SUBMAP_HEIGHT,
    geometries: geos,
    markers: [],
    legend,
    caption: '自然地形のひろがり',
    emptyMessage: 'この付近では自然地形の形状データが取得できませんでした。',
  };
}

function buildJshisMap(ground: GroundSummary, site: Point): MapSpec | null {
  const jshis = ground.jshis_surface_ground;
  const meshes = jshis.area_meshes ?? [];
  if (!jshis.geometry && meshes.length === 0) return null;

  const geos: GeometryLayer[] = [];
  const seenRanks = new Set<ArvRank>();

  // Surrounding meshes — fill colour = ARV (揺れやすさ) rank, forming a heatmap of the extent.
  for (const mesh of meshes) {
    if (!mesh.geometry) continue;
    geos.push({
      data: mesh.geometry,
      style: {
        stroke: false,
        fillColor: ARV_RANK_COLOR[mesh.arv_rank] ?? ARV_RANK_COLOR.Unknown,
        fillOpacity: 0.5,
      },
      label:
        mesh.arv != null
          ? `ARV ${mesh.arv.toFixed(2)}（${ARV_RANK_LABEL[mesh.arv_rank]}）`
          : '揺れやすさ不明',
    });
    seenRanks.add(mesh.arv_rank);
  }

  // Site mesh on top, outlined so the buyer can locate their parcel within the heatmap.
  if (jshis.geometry) {
    geos.push({
      data: jshis.geometry,
      style: {
        stroke: true,
        color: '#2C2C2C',
        weight: 2,
        fillColor: ARV_RANK_COLOR[jshis.arv_rank] ?? ARV_RANK_COLOR.Unknown,
        fillOpacity: 0.75,
      },
      label: `この地点｜ARV ${jshis.arv != null ? jshis.arv.toFixed(2) : '—'}（${jshis.arv_label}）${
        jshis.microtopography_name ? ` / ${jshis.microtopography_name}` : ''
      }`,
    });
    seenRanks.add(jshis.arv_rank);
  }

  const RANK_ORDER: ArvRank[] = ['A', 'B', 'C', 'D', 'E', 'Unknown'];
  const legend: LegendItem[] = RANK_ORDER.filter((r) => seenRanks.has(r)).map((r) => ({
    color: ARV_RANK_COLOR[r],
    label: `${r}: ${ARV_RANK_LABEL[r]}`,
  }));

  return {
    key: 'jshis',
    point: site,
    tileUrl: TILES.std,
    zoom: 15,
    height: SUBMAP_HEIGHT,
    geometries: geos,
    markers: [],
    legend,
    caption: 'J-SHIS 表層地盤メッシュ｜揺れやすさ(ARV)',
    emptyMessage: 'この付近では J-SHIS 表層地盤メッシュのデータが取得できませんでした。',
  };
}

export type GroundContent = {
  lead: LeadSpec;
  maps: MapSpec[];
  rows: RowSpec[];
  checkTitle: string;
  checkItems: string[];
};

// 地盤章の中身。画面 (ChapterGround) と PDF 冊子が共有する純関数。
export function buildGroundContent(ground: GroundSummary, site: Point): GroundContent {
  const gsi = ground.gsi_landform;
  const jshis = ground.jshis_surface_ground;
  const maps: MapSpec[] = [buildLandformMap(ground, site)];
  const jshisMap = buildJshisMap(ground, site);
  if (jshisMap) maps.push(jshisMap);

  const rows: RowSpec[] = [
    {
      icon: 'mountain',
      label: `自然地形: ${gsi.category ?? '不明'}`,
      detail: gsi.risk_comment ?? gsi.origin ?? undefined,
      status: rankToStatus(gsi.rank),
      statusLabel: gsi.buyer_label || rankToStatusLabel(gsi.rank),
    },
    {
      icon: 'layers-subtract',
      label: `微地形: ${jshis.microtopography_name ?? '不明'}`,
      status: rankToStatus(jshis.rank),
      statusLabel: rankToStatusLabel(jshis.rank),
    },
    {
      icon: 'wave-sine',
      label: '地盤増幅率 (ARV)',
      detail: jshis.arv != null ? `ARV ${jshis.arv.toFixed(2)} / ${jshis.arv_label}` : '未取得',
      status: rankToStatus(jshis.rank),
      statusLabel: rankToStatusLabel(jshis.rank),
    },
    {
      icon: 'ripple',
      label: 'AVS30 (上層 30m 平均S波速度)',
      detail: jshis.softness_label,
      status: 'neutral',
      statusLabel: jshis.avs30 != null ? `${jshis.avs30} m/s` : '未取得',
    },
  ];

  return {
    lead: { label: ground.label, message: ground.buyer_message },
    maps,
    rows,
    checkTitle: CHECK_TITLE,
    checkItems: ground.check_items,
  };
}

export default function ChapterGround({ ground, site }: Props) {
  const dev = useDevMode();
  const content = useMemo(() => buildGroundContent(ground, site), [ground, site]);

  return (
    <ChapterSection id="ground">
      <ChapterLead {...content.lead} />

      {content.maps.map((spec) => (
        <ChapterMap key={spec.key} spec={spec} />
      ))}

      <RowList rows={content.rows} />

      <CheckList items={content.checkItems} title={content.checkTitle} />

      {dev && (
        <details className={styles.details}>
          <summary>地盤の生データを見る (dev)</summary>
          <pre className={styles.json}>
            {JSON.stringify({ gsi: ground.gsi_landform, jshis: ground.jshis_surface_ground }, null, 2)}
          </pre>
        </details>
      )}
    </ChapterSection>
  );
}
